import { useEffect, useMemo, useState } from "react";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Toaster, toast } from "sonner";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Search, Sparkles, Upload, ImageOff, ExternalLink, Loader2 } from "lucide-react";

// Thumbnail with React-state-based error handling (avoids DOM mutation bugs)
function ERPThumb({ url }) {
  const [broken, setBroken] = useState(false);
  if (broken) {
    return (
      <div className="w-14 h-14 rounded-md bg-zinc-100 border border-zinc-200 flex items-center justify-center">
        <ImageOff className="w-4 h-4 text-zinc-400" />
      </div>
    );
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="block w-14 h-14 rounded-md overflow-hidden border border-zinc-200 hover:ring-2 hover:ring-[#002FA7] transition-all"
    >
      <img
        src={url}
        alt=""
        className="w-full h-full object-cover"
        onError={() => setBroken(true)}
      />
    </a>
  );
}

export default function ERPSync() {
  const [providers, setProviders] = useState([]);
  const [saved, setSaved] = useState([]);
  const [provider, setProvider] = useState("");
  const [category, setCategory] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [products, setProducts] = useState([]); // list of {id, code, name, description, image, images?, status?, source?}
  const [searchingFor, setSearchingFor] = useState({}); // productId → bool
  const [pushingFor, setPushingFor] = useState({}); // productId → bool

  useEffect(() => {
    (async () => {
      const [p, s] = await Promise.all([
        api.get("/erp/providers"),
        api.get("/erp/credentials"),
      ]);
      setProviders(p.data);
      setSaved(s.data);
      // Persist selection: prefer localStorage → first connected
      const saved_key = localStorage.getItem("erpSync.provider") || "";
      const isSavedValid = s.data.some((c) => c.provider === saved_key);
      const next = isSavedValid ? saved_key : (s.data[0]?.provider || "");
      if (next) setProvider(next);
    })();
  }, []);

  // Persist selection whenever user changes it
  useEffect(() => {
    if (provider) {
      try { localStorage.setItem("erpSync.provider", provider); } catch (_) {}
    }
  }, [provider]);

  const connectedProviders = useMemo(
    () => providers.filter((p) => saved.some((s) => s.provider === p.key)),
    [providers, saved]
  );

  const loadProducts = async (pageOverride) => {
    if (!provider) {
      toast.error("Selecione um ERP conectado");
      return;
    }
    const nextPage = pageOverride ?? 1;
    setLoadingProducts(true);
    try {
      const { data } = await api.get(`/erp/${provider}/products`, {
        params: { search: query, page: nextPage },
      });
      setProducts((data.items || []).map((it) => ({ ...it })));
      setPage(nextPage);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao consultar ERP");
    } finally {
      setLoadingProducts(false);
    }
  };

  const findImages = async (product) => {
    setSearchingFor((s) => ({ ...s, [product.id]: true }));
    try {
      const { data } = await api.post("/search", {
        items: [{ name: product.name || product.code, category }],
      });
      const r = (data && data[0]) || null;
      setProducts((list) =>
        list.map((p) =>
          p.id === product.id
            ? { ...p, foundImages: r?.images || [], foundSource: r?.source || "", foundStatus: r?.status || "not_found" }
            : p
        )
      );
      if (!r?.images?.length) toast.warning(`Nada encontrado para ${product.name}`);
      else toast.success(`${r.images.length} imagem(ns) encontradas para ${product.name}`);
    } catch (e) {
      toast.error("Erro na busca de imagens");
    } finally {
      setSearchingFor((s) => ({ ...s, [product.id]: false }));
    }
  };

  const pushImages = async (product) => {
    if (!product.foundImages?.length) {
      toast.error("Busque imagens primeiro");
      return;
    }
    setPushingFor((s) => ({ ...s, [product.id]: true }));
    try {
      const { data } = await api.post(`/erp/${provider}/products/images`, {
        product_id: product.id,
        image_urls: product.foundImages,
      });
      if (data.ok) {
        toast.success(data.message || "Imagens enviadas ao ERP");
        setProducts((list) =>
          list.map((p) => (p.id === product.id ? { ...p, pushed: true, pushMessage: data.message } : p))
        );
      } else {
        toast.error(data.message || "Falha ao enviar");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao enviar imagens");
    } finally {
      setPushingFor((s) => ({ ...s, [product.id]: false }));
    }
  };

  const runBatch = async () => {
    if (!products.length) return;
    toast.info(`Processando ${products.length} produtos em lote...`);
    for (const product of products) {
      if (!product.foundImages?.length) {
        await findImages(product);
      }
    }
    toast.success("Busca em lote concluída. Revise antes de enviar ao ERP.");
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-[#F8F9FA]">
      <Sidebar />
      <main className="flex-1 p-6 sm:p-10">
        <Toaster position="top-right" richColors />

        <header className="mb-8">
          <div className="eyebrow mb-2">ERP Sync</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#09090B]">
            Popular fichas de produto no ERP
          </h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
            Selecione um ERP conectado, busque produtos, encontre imagens e envie de volta.
            Configure credenciais em{" "}
            <Link to="/settings" className="brand-text font-medium underline">
              Configurações
            </Link>
            .
          </p>
        </header>

        {connectedProviders.length === 0 ? (
          <div className="border border-dashed border-zinc-300 rounded-md p-12 text-center bg-white">
            <div className="eyebrow mb-2">Nenhum ERP conectado</div>
            <p className="text-sm text-zinc-500 mb-4">
              Configure credenciais do Bling, Tiny ou Omie para começar.
            </p>
            <Link to="/settings">
              <Button className="rounded-md brand-primary text-white" data-testid="go-to-settings-button">
                Ir para Configurações
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <section className="border border-zinc-200 rounded-md bg-white p-5 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                <div className="md:col-span-3">
                  <div className="eyebrow mb-1">ERP</div>
                  <Select value={provider} onValueChange={setProvider}>
                    <SelectTrigger data-testid="erp-select">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {connectedProviders.map((p) => (
                        <SelectItem key={p.key} value={p.key}>
                          {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-4">
                  <div className="eyebrow mb-1">Categoria (usada na busca)</div>
                  <Input
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    placeholder="ex: medicamento, cosmético, eletrônico"
                    className="h-10 rounded-md border-zinc-300"
                    data-testid="erp-category-input"
                  />
                </div>
                <div className="md:col-span-3">
                  <div className="eyebrow mb-1">Buscar produto no ERP</div>
                  <Input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Nome ou código"
                    onKeyDown={(e) => e.key === "Enter" && loadProducts(1)}
                    className="h-10 rounded-md border-zinc-300"
                    data-testid="erp-search-input"
                  />
                </div>
                <div className="md:col-span-2">
                  <Button
                    onClick={() => loadProducts(1)}
                    disabled={loadingProducts || !provider}
                    className="w-full h-10 rounded-md brand-primary text-white"
                    data-testid="erp-load-products-button"
                  >
                    {loadingProducts ? "Buscando..." : "Listar produtos"}
                  </Button>
                </div>
              </div>
            </section>

            {products.length > 0 && (
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-xl font-semibold text-[#09090B]">
                  {products.length} produtos · página {page}
                </h2>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => loadProducts(Math.max(1, page - 1))}
                    disabled={page <= 1 || loadingProducts}
                    className="rounded-md border-zinc-300"
                    data-testid="erp-prev-page-button"
                  >
                    Anterior
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => loadProducts(page + 1)}
                    disabled={loadingProducts}
                    className="rounded-md border-zinc-300"
                    data-testid="erp-next-page-button"
                  >
                    Próxima
                  </Button>
                  <Button
                    onClick={runBatch}
                    className="rounded-md brand-primary text-white"
                    data-testid="erp-batch-search-button"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Buscar todas
                  </Button>
                </div>
              </div>
            )}

            <div className="space-y-3">
              {products.map((product) => (
                <div
                  key={product.id}
                  className="border border-zinc-200 rounded-md bg-white p-4 flex flex-col lg:flex-row lg:items-center gap-4"
                  data-testid={`erp-product-${product.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-zinc-400 font-mono">#{product.code || product.id}</div>
                    <div className="font-medium text-zinc-900 truncate" title={product.name}>
                      {product.name}
                    </div>
                    {product.description && (
                      <div className="text-xs text-zinc-500 truncate mt-0.5">{product.description}</div>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    {(product.foundImages || []).slice(0, 3).map((u, i) => (
                      <ERPThumb key={`${i}-${u}`} url={u} />
                    ))}
                    {!(product.foundImages || []).length &&
                      [0, 1, 2].map((i) => (
                        <div
                          key={i}
                          className="w-14 h-14 rounded-md bg-zinc-100 border border-zinc-200 flex items-center justify-center"
                        >
                          <ImageOff className="w-4 h-4 text-zinc-400" />
                        </div>
                      ))}
                  </div>

                  <div className="flex flex-col gap-2 min-w-[220px]">
                    <Button
                      onClick={() => findImages(product)}
                      disabled={!!searchingFor[product.id]}
                      variant="outline"
                      className="rounded-md border-zinc-300"
                      data-testid={`erp-find-images-${product.id}`}
                    >
                      {searchingFor[product.id] ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Buscando...
                        </>
                      ) : (
                        <>
                          <Search className="w-4 h-4 mr-2" />
                          Buscar imagens
                        </>
                      )}
                    </Button>
                    <Button
                      onClick={() => pushImages(product)}
                      disabled={!product.foundImages?.length || !!pushingFor[product.id]}
                      className={`rounded-md ${
                        product.pushed
                          ? "bg-emerald-600 hover:bg-emerald-700"
                          : "brand-primary"
                      } text-white transition-colors`}
                      data-testid={`erp-push-images-${product.id}`}
                    >
                      {pushingFor[product.id] ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Enviando...
                        </>
                      ) : product.pushed ? (
                        <>
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Enviado
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Enviar ao ERP
                        </>
                      )}
                    </Button>
                    {product.foundSource && (
                      <div className="text-[10px] font-mono text-zinc-400 text-right">
                        {product.foundSource}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
